<!--
  Transcript + orb layout
  ────────────────────────
  Orb centered on screen. Transcript panel positioned absolutely to the right
  with a fixed height so it never shifts the orb or jumps as text streams in.
  Header bar with toggle always visible.
-->
<template>
    <div class="relative flex flex-col items-center gap-3 md:block">
        <!-- Orb (always centered — transcript doesn't affect its position) -->
        <!-- pb-6 on mobile clears the marble's scale(1.25) visual overflow on the bottom -->
        <div class="pb-6 md:pb-0">
            <slot />
        </div>

        <!-- Optional controls directly under the orb (e.g. pause / resume) — centered in both
             the mobile (flex-col) and desktop (block) layouts. -->
        <div class="flex justify-center mt-10">
            <slot name="controls" />
        </div>

        <!-- Outside the translucent panel: status text needs strong ink on the voice canvas. -->
        <Transition name="transcript-fade" mode="out-in">
            <p v-if="statusText" :key="statusText" class="status-text body-small text-ink font-medium">
                {{ statusText }}
            </p>
        </Transition>

        <!-- Transcript panel — absolute-positioned to the right of the orb on desktop -->
        <BaseCollapsible
            :open="!hidden"
            stretched-trigger
            :trigger-aria-label="hidden ? t('components.transcript.show') : t('components.transcript.hide')"
            class="transcript-panel pointer-events-auto"
            :class="{ 'transcript-panel-expanded': !hidden }"
            @update:open="$emit('toggle-hidden')">
            <!-- Header bar — icon-only when collapsed, full header when expanded -->
            <template #trigger>
                <div
                    class="transcript-header"
                    :class="{ 'transcript-header-justify-start': hidden }"
                    :title="hidden ? t('components.transcript.show') : t('components.transcript.hide')">
                    <div class="flex items-center gap-2">
                        <i class="bi bi-chat-text text-sm text-ink-muted" aria-hidden="true"></i>
                        <span class="details font-semibold text-ink-muted uppercase tracking-wide whitespace-nowrap">{{
                            hidden ? t("components.transcript.view") : t("components.transcript.title")
                        }}</span>
                    </div>
                    <i v-if="!hidden" class="bi bi-chevron-up text-sm text-ink-muted" aria-hidden="true"></i>
                </div>
            </template>

            <!-- Scrollable transcript content — fixed height -->
            <template #content>
                <div class="transcript-content">
                    <!-- AI transcript -->
                    <Transition name="transcript-fade">
                        <div
                            v-if="transcripts.assistant.visible && transcripts.assistant.text"
                            class="transcript-card transcript-assistant"
                            :class="{ 'transcript-interrupted': transcripts.assistant.interrupted, 'transcript-frozen': frozen }">
                            <span class="transcript-role transcript-role-ai">{{ t("components.transcript.nadia") }}</span>
                            <p class="body-small text-ink">
                                <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -- punctuation: ellipsis marking an interrupted sentence -->
                                {{ transcripts.assistant.text }}<span v-if="transcripts.assistant.interrupted" class="text-ink-muted">…</span>
                            </p>
                        </div>
                    </Transition>

                    <!-- User transcript -->
                    <Transition name="transcript-fade">
                        <div
                            v-if="transcripts.user.visible && transcripts.user.text"
                            class="transcript-card transcript-user"
                            :class="{ 'transcript-interrupted': transcripts.user.interrupted, 'transcript-frozen': frozen }">
                            <span class="transcript-role transcript-role-user">{{ t("components.transcript.you") }}</span>
                            <p class="body-small text-ink-muted">
                                {{ transcripts.user.text }}
                            </p>
                        </div>
                    </Transition>
                </div>
            </template>
        </BaseCollapsible>
    </div>
</template>

<script setup lang="ts">
import BaseCollapsible from "~vue/components/BaseCollapsible.vue";
import type { Transcripts } from "~vue/composables/useRealtimeSession";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

defineProps<{
    transcripts: Transcripts;
    statusText: string;
    /** When true (reconnecting), transcripts are dimmed/frozen in place */
    frozen?: boolean;
    /** When true, transcript panel is collapsed */
    hidden?: boolean;
}>();

defineEmits<{
    (e: "toggle-hidden"): void;
}>();
</script>

<style scoped>
@reference "../../../css/tailwind.css";

/* Desktop: absolute to the right of the orb */
.transcript-panel {
    @apply flex flex-col;
    @apply rounded-2xl bg-white/70 backdrop-blur-md shadow-sm;

    /* Mobile: stacked below status text */
    @apply relative mx-auto;
}

.transcript-panel-expanded {
    /* Fixed width when expanded so content changes don't cause layout shift */
    @apply w-[min(320px,calc(100vw-2rem))];
}

/* Desktop override: absolute-positioned to the right */
@media (min-width: 768px) {
    .transcript-panel {
        @apply absolute top-1/2 -translate-y-1/2;
        @apply mt-0 mx-0;
        left: calc(100% + 4.5rem);
    }

    .transcript-panel-expanded {
        width: clamp(260px, 25vw, 380px);
    }
}

.transcript-header {
    @apply flex items-center justify-between px-3 py-2 cursor-pointer;
    @apply hover:bg-white/50 rounded-t-2xl transition-colors;
}

.transcript-header-justify-start {
    @apply justify-start rounded-2xl;
}

.transcript-content {
    @apply flex flex-col gap-2 px-3 pb-3 overflow-y-auto;
    height: 220px;
}

.transcript-card {
    @apply px-3 py-2 rounded-xl;
    @apply transition-opacity duration-300;
}

.transcript-user {
    @apply bg-surface-hover/60;
}

.transcript-assistant {
    @apply bg-surface-sunken;
}

.transcript-role {
    @apply details font-bold uppercase tracking-wide block mb-1;
}

.transcript-role-ai {
    @apply text-ink;
}

.transcript-role-user {
    @apply text-ink-muted;
}

/* Status text: in-flow on mobile, absolute below orb on desktop */
.status-text {
    @apply text-center max-w-[calc(100vw-2rem)] whitespace-normal;
}

@media (min-width: 768px) {
    .status-text {
        @apply absolute left-1/2 -translate-x-1/2;
        top: calc(100% + 3rem);
        margin-top: 0;
    }
}

/* Interrupted: dim the transcript to acknowledge the barge-in */
.transcript-interrupted {
    @apply opacity-50;
    transition: opacity 0.2s ease;
}

/* Frozen: transcripts stay visible but dimmed during reconnection */
.transcript-frozen {
    @apply opacity-40;
}

/* Fade transition */
.transcript-fade-enter-active,
.transcript-fade-leave-active {
    transition: opacity 0.3s ease;
}
.transcript-fade-enter-from,
.transcript-fade-leave-to {
    opacity: 0;
}
</style>
